import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ArchiveRestore, Trash2, Layout } from 'lucide-react';
import useBoardStore from '../store/boardStore';
import useAuthStore from '../store/authStore';
import formatRelative from '../utils/formatRelative';
import { dialog } from '../store/dialogStore';
import { isOrgAdmin } from '../utils/permissions';

const TABS = [
  { id: 'cards', label: 'Itens' },
  { id: 'groups', label: 'Grupos' },
  { id: 'boards', label: 'Quadros' },
];

export default function TrashPage() {
  const {
    fetchGlobalTrash,
    unarchiveCard, deleteCard,
    restoreGroup, deleteGroup,
    restoreBoard, deleteBoard,
    fetchBoards,
  } = useBoardStore();
  const user = useAuthStore((s) => s.user);

  const [tab, setTab] = useState('cards');
  const [data, setData] = useState({ cards: [], groups: [], boards: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const result = await fetchGlobalTrash();
      setData(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isOrgAdmin(user)) load(); }, []);

  const items = data[tab] ?? [];

  const handleRestoreCard = async (id) => {
    await unarchiveCard(id);
    setData((d) => ({ ...d, cards: d.cards.filter((c) => c.id !== id) }));
  };
  const handleDeleteCard = async (id) => {
    if (!(await dialog.confirm('Excluir permanentemente', 'Esta ação não pode ser desfeita.', { confirmLabel: 'Excluir', danger: true }))) return;
    await deleteCard(id);
    setData((d) => ({ ...d, cards: d.cards.filter((c) => c.id !== id) }));
  };

  const handleRestoreGroup = async (id) => {
    await restoreGroup(id);
    setData((d) => ({ ...d, groups: d.groups.filter((g) => g.id !== id) }));
  };
  const handleDeleteGroup = async (id) => {
    if (!(await dialog.confirm('Excluir grupo', 'O grupo e todos os seus itens serão excluídos permanentemente.', { confirmLabel: 'Excluir', danger: true }))) return;
    await deleteGroup(id);
    setData((d) => ({ ...d, groups: d.groups.filter((g) => g.id !== id) }));
  };

  const handleRestoreBoard = async (id, workspaceId) => {
    await restoreBoard(id);
    if (fetchBoards && workspaceId) fetchBoards(workspaceId);
    setData((d) => ({ ...d, boards: d.boards.filter((b) => b.id !== id) }));
  };
  const handleDeleteBoard = async (id) => {
    if (!(await dialog.confirm('Excluir quadro', 'O quadro e todo o seu conteúdo serão excluídos permanentemente.', { confirmLabel: 'Excluir', danger: true }))) return;
    await deleteBoard(id);
    setData((d) => ({ ...d, boards: d.boards.filter((b) => b.id !== id) }));
  };

  if (!isOrgAdmin(user)) return <Navigate to="/" replace />;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-text-primary">Lixeira</h1>
        <p className="text-sm text-text-secondary mt-1">
          Itens arquivados de todos os boards da organização.
        </p>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <div className="flex border-b border-border px-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                tab === t.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {t.label}
              {data[t.id]?.length > 0 && (
                <span className="ml-1.5 text-[11px] bg-white/10 px-1.5 py-0.5 rounded-full">
                  {data[t.id].length}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-center py-12 text-sm text-text-secondary">Carregando...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-sm text-text-secondary">Lixeira vazia.</div>
          ) : (
            <div className="space-y-2">
              {tab === 'cards' && items.map((c) => (
                <div key={c.id} className="flex items-center gap-3 bg-main border border-border/50 rounded-lg p-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary truncate">{c.title}</div>
                    <div className="text-[11px] text-text-secondary">
                      {c.board_name} › {c.group_name} · Arquivado {formatRelative(c.archived_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRestoreCard(c.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-accent hover:bg-accent/10 rounded transition-colors shrink-0"
                  >
                    <ArchiveRestore className="w-3.5 h-3.5" />
                    Restaurar
                  </button>
                  <button
                    onClick={() => handleDeleteCard(c.id)}
                    className="p-1.5 text-text-secondary hover:text-danger hover:bg-white/5 rounded transition-colors shrink-0"
                    title="Excluir permanentemente"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {tab === 'groups' && items.map((g) => (
                <div key={g.id} className="flex items-center gap-3 bg-main border border-border/50 rounded-lg p-3">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: g.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary truncate">{g.name}</div>
                    <div className="text-[11px] text-text-secondary">
                      {g.board_name} · {Number(g.card_count)} {Number(g.card_count) === 1 ? 'item' : 'itens'} · Arquivado {formatRelative(g.archived_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRestoreGroup(g.id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-accent hover:bg-accent/10 rounded transition-colors shrink-0"
                  >
                    <ArchiveRestore className="w-3.5 h-3.5" />
                    Restaurar
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(g.id)}
                    className="p-1.5 text-text-secondary hover:text-danger hover:bg-white/5 rounded transition-colors shrink-0"
                    title="Excluir permanentemente"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}

              {tab === 'boards' && items.map((b) => (
                <div key={b.id} className="flex items-center gap-3 bg-main border border-border/50 rounded-lg p-3">
                  <Layout className="w-4 h-4 text-text-secondary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-text-primary truncate">{b.name}</div>
                    <div className="text-[11px] text-text-secondary">
                      {b.workspace_name} · Arquivado {formatRelative(b.archived_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRestoreBoard(b.id, b.workspace_id)}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-accent hover:bg-accent/10 rounded transition-colors shrink-0"
                  >
                    <ArchiveRestore className="w-3.5 h-3.5" />
                    Restaurar
                  </button>
                  <button
                    onClick={() => handleDeleteBoard(b.id)}
                    className="p-1.5 text-text-secondary hover:text-danger hover:bg-white/5 rounded transition-colors shrink-0"
                    title="Excluir permanentemente"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
